import { useMemo, useRef, useState } from 'react';
import { Form, Modal } from 'antd';
import { RevxButton } from 'components/RevxButton';
import {
  CostDistributionSection,
  CostDistributionSectionRef,
} from 'features/ShoppingCart/components/CostDistributionSection';
import { CostDistributionPayload, PurchaseOrder } from 'domains/orderDomain';
import { useOrg, useUser } from 'hooks';
import './JobAdjustmentOracleBillingCodeModal.less';

type Props = {
  jobId: number;
  jobName: string;
  quantity?: number;
  totalCharge?: number;
  costDistribution?: CostDistributionPayload;
  onCancel: () => void;
  onSubmit: (payload: CostDistributionPayload) => Promise<void>;
};

const JobAdjustmentOracleBillingCodeModal = function ({
  jobId,
  jobName,
  quantity,
  totalCharge,
  costDistribution,
  onCancel,
  onSubmit,
}: Props) {
  const org = useOrg();
  const user = useUser();
  const [billingDetailForm] = Form.useForm();
  const costDistributionRef = useRef<CostDistributionSectionRef>(null);
  const [saving, setSaving] = useState(false);

  // The adjustment always covers a single job, so the saved distribution is
  // narrowed down to this job and re-keyed as a single-job distribution.
  const initialPayload = useMemo<CostDistributionPayload | undefined>(() => {
    if (!costDistribution) return undefined;
    const categories = (costDistribution.categories || [])
      .filter(
        (category) =>
          category.jobId === undefined || Number(category.jobId) === jobId
      )
      .map((category) => ({ ...category, jobId: undefined }));

    return {
      ...costDistribution,
      categories,
      isApplyToAllJobs: true,
    };
  }, [costDistribution, jobId]);

  const orderItems = useMemo(
    () =>
      ({
        jobs: [
          {
            jobId,
            jobName,
            quantity,
            totalCharge,
          },
        ],
      } as unknown as Partial<PurchaseOrder>),
    [jobId, jobName, quantity, totalCharge]
  );

  const submitHandler = async () => {
    const payload = costDistributionRef.current?.getPayload();
    // getPayload surfaces its own validation errors and returns undefined
    if (!payload) return;
    try {
      setSaving(true);
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      centered
      width={1000}
      maskClosable={false}
      title="Edit Oracle Billing Code"
      className="job-adjustment-oracle-modal"
      onCancel={onCancel}
      footer={
        <>
          <RevxButton type="default" brandGreen onClick={onCancel}>
            Cancel
          </RevxButton>
          <RevxButton
            type="primary"
            brandGreen
            loading={saving}
            onClick={submitHandler}
          >
            Save
          </RevxButton>
        </>
      }
    >
      <CostDistributionSection
        compact
        hideProfileOptions
        ref={costDistributionRef}
        org={org}
        user={user}
        orderItems={orderItems}
        formTotal={{ orderTotal: totalCharge }}
        billingDetailForm={billingDetailForm}
        initialPayload={initialPayload}
      />
    </Modal>
  );
};

export default JobAdjustmentOracleBillingCodeModal;
